import React, { useState } from 'react';
import { FieldDefinition } from '../../types/schema';
import { COMPARE_MODES } from '../../lib/compareModes';
import LLMInstructionsEditor from './LLMInstructionsEditor';

interface Props {
  fieldName: string;
  field: FieldDefinition;
  sections: Record<string, { label_fr: string; order: number; icon: string }>;
  onChange: (field: FieldDefinition) => void;
  globalActiveTab?: 'general' | 'scoring' | 'llm' | null;
  defaultActiveTab?: 'general' | 'scoring' | 'llm';
  onTabChanged?: () => void;
}

const WEIGHT_OPTIONS = [
  { value: 0, label: 'Exclu (0)', color: 'bg-gray-200' },
  { value: 1, label: 'Mineur (1)', color: 'bg-blue-200' },
  { value: 2, label: 'Standard (2)', color: 'bg-green-200' },
  { value: 3, label: 'Critique (3)', color: 'bg-red-200' },
];


const FIELD_TYPES = [
  { value: 'string', label: 'Texte (string)' },
  { value: 'number', label: 'Nombre décimal (number)' },
  { value: 'integer', label: 'Nombre entier (integer)' },
  { value: 'boolean', label: 'Booléen (boolean)' },
];

const WIDTH_OPTIONS = [
  { value: 'full', label: 'Pleine largeur (100%)' },
  { value: '3/4', label: 'Trois quarts (75%)' },
  { value: '2/3', label: 'Deux tiers (66%)' },
  { value: '1/2', label: 'Demi (50%)' },
  { value: '1/3', label: 'Tiers (33%)' },
  { value: '1/4', label: 'Quart (25%)' },
];

const FieldEditor: React.FC<Props> = ({ field, sections, onChange, globalActiveTab, defaultActiveTab, onTabChanged }) => {
  const [activeTab, setActiveTab] = useState<'general' | 'scoring' | 'llm'>(defaultActiveTab || 'general');

  // React to globalActiveTab changes
  React.useEffect(() => {
    if (globalActiveTab) {
      setActiveTab(globalActiveTab);
      // Notify parent that tab was changed
      if (onTabChanged) {
        onTabChanged();
      }
    }
  }, [globalActiveTab, onTabChanged]);

  const updateUI = (key: string, value: any) => {
    onChange({
      ...field,
      ui: { ...field.ui, [key]: value }
    });
  };

  const updateScoring = (key: string, value: any) => {
    onChange({
      ...field,
      scoring: { ...field.scoring, [key]: value }
    });
  };

  const updateLLM = (llmConfig: any) => {
    onChange({
      ...field,
      llm: llmConfig
    });
  };

  return (
    <div>
      {/* Tabs */}
      <div className="flex border-b mb-4">
        {(['general', 'scoring', 'llm'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'general' && 'Général'}
            {tab === 'scoring' && 'Scoring'}
            {tab === 'llm' && 'Instructions LLM'}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label (FR)
            </label>
            <input
              type="text"
              value={field.ui.label_fr}
              onChange={(e) => updateUI('label_fr', e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section
            </label>
            <select
              value={field.ui.section}
              onChange={(e) => updateUI('section', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {Object.entries(sections)
                .sort((a, b) => a[1].order - b[1].order)
                .map(([key, section]) => (
                  <option key={key} value={key}>
                    {section.label_fr}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type d'input
            </label>
            <select
              value={field.ui.input_type}
              onChange={(e) => updateUI('input_type', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="text">Texte</option>
              <option value="textarea">Zone de texte</option>
              <option value="number">Nombre</option>
              <option value="date">Date</option>
              <option value="select">Sélection</option>
              <option value="checkbox">Checkbox</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordre d'affichage
            </label>
            <input
              type="number"
              value={field.ui.display_order}
              onChange={(e) => updateUI('display_order', parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Largeur
            </label>
            <select
              value={field.ui.width || 'full'}
              onChange={(e) => updateUI('width', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {WIDTH_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Texte d'aide
            </label>
            <input
              type="text"
              value={field.ui.help_text || ''}
              onChange={(e) => updateUI('help_text', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Aide contextuelle pour l'utilisateur"
            />
          </div>
        </div>
      )}

      {/* Scoring Tab */}
      {activeTab === 'scoring' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Poids du scoring
            </label>
            <div className="flex gap-2">
              {WEIGHT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => updateScoring('weight', opt.value)}
                  className={`px-4 py-2 rounded border ${
                    field.scoring.weight === opt.value
                      ? `${opt.color} border-gray-400 font-medium`
                      : 'bg-white hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode de comparaison
            </label>
            <select
              value={field.scoring.compare_mode}
              onChange={(e) => updateScoring('compare_mode', e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {COMPARE_MODES.map(mode => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
            {(() => {
              const desc = COMPARE_MODES.find(m => m.value === field.scoring.compare_mode)?.description;
              return desc ? <p className="text-xs text-gray-500 mt-1">{desc}</p> : null;
            })()}
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${
              field.scoring.compare_mode === 'numeric_tolerance' ? 'text-gray-700' : 'text-gray-400'
            }`}>
              Tolérance (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={field.scoring.tolerance_percent ?? 0}
              onChange={(e) => updateScoring('tolerance_percent', parseFloat(e.target.value) || 0)}
              disabled={field.scoring.compare_mode !== 'numeric_tolerance'}
              className={`w-full border rounded px-3 py-2 ${
                field.scoring.compare_mode !== 'numeric_tolerance' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''
              }`}
            />
            <p className={`text-xs mt-1 ${
              field.scoring.compare_mode === 'numeric_tolerance' ? 'text-gray-500' : 'text-gray-400'
            }`}>
              0% = comparaison exacte, 1% = ±1% de la valeur attendue
            </p>
          </div>

          {field.scoring.weight === 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
              ⚠️ Ce champ est exclu du calcul du score. Il sera affiché mais ne contribuera pas au score global.
            </div>
          )}
        </div>
      )}

      {/* LLM Tab */}
      {activeTab === 'llm' && (
        <div className="space-y-4">
          {/* Type selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de données (JSON Schema)
            </label>
            <select
              value={field.type}
              onChange={(e) => onChange({ ...field, type: e.target.value })}
              className="w-full border rounded px-3 py-2"
            >
              {FIELD_TYPES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Détermine le type attendu dans le JSON retourné par le LLM
            </p>
          </div>

          {/* Required checkbox — déplacé du tab Général en PROD1.7.7.2.
              `required: true` est sérialisé dans le tableau `required: [...]` du
              JSON Schema envoyé au LLM. Aide le LLM à ne pas omettre le champ
              (effet observé sur prix_de_vente_adjuste — run 2d765e0d). */}
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => onChange({ ...field, required: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700">Obligatoire</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Sérialisé dans le tableau <code>required</code> du JSON Schema envoyé au LLM.
              Encourage le LLM à toujours retourner ce champ.
            </p>
          </div>

          {/* Separator */}
          <hr className="border-gray-200" />

          {/* LLM Instructions */}
          <LLMInstructionsEditor
            config={field.llm}
            onChange={updateLLM}
          />
        </div>
      )}
    </div>
  );
};

export default FieldEditor;
